"use client";

import { useState } from "react";
import Image from "next/image";
import { Search, ShoppingBag } from "lucide-react";

import { useCart } from "@/context/cart-context";
import { ProductGrid } from "@/components/store/product-card";
import type { StoreCollection, StoreProduct } from "@/components/store/types";

type StoreClientProps = Readonly<{
  collections: readonly StoreCollection[];
}>;

const getCategoryLabel = (product: StoreProduct): string => {
  return product.category || "Merch";
};

export const StoreClient = ({
  collections,
}: StoreClientProps): React.ReactElement => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");
  const { cartCount, setCartOpen } = useCart();
  const availableCollections = collections.filter(
    (collection) => collection.products.length > 0,
  );
  const products = availableCollections.flatMap(
    (collection) => collection.products,
  );

  const categories = [
    "All",
    ...Array.from(new Set(products.map(getCategoryLabel))).sort(),
  ];

  const normalizedQuery = query.trim().toLowerCase();
  const visibleCollections = availableCollections.map((collection) => ({
    ...collection,
    products: collection.products.filter((product) => {
      const matchesCategory =
        activeCategory === "All" ||
        getCategoryLabel(product) === activeCategory;
      const matchesQuery =
        normalizedQuery.length === 0 ||
        product.name.toLowerCase().includes(normalizedQuery) ||
        product.description.toLowerCase().includes(normalizedQuery);

      return matchesCategory && matchesQuery;
    }),
  }));
  const visibleProductCount = visibleCollections.reduce(
    (count, collection) => count + collection.products.length,
    0,
  );

  if (products.length === 0) {
    return (
      <div className="border-border bg-primary/5 mx-auto grid max-w-4xl overflow-hidden rounded-2xl border md:grid-cols-[0.9fr_1.1fr]">
        <div className="bg-primary/10 relative min-h-72">
          <Image
            src="/images/fourthwall-social.png"
            alt="SimpleBLE Store"
            fill
            sizes="(max-width: 768px) 100vw, 40vw"
            className="object-cover"
          />
        </div>
        <div className="flex flex-col justify-center p-6 text-center md:p-10 md:text-left">
          <h2 className="font-sora text-foreground text-2xl md:text-3xl">
            The shelves are being stocked.
          </h2>
          <p className="text-secondary mt-4 leading-7">
            New gear lands here the moment it&apos;s live.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-12 md:gap-16">
      <div className="border-border rounded-[1.5rem] border bg-[#fafafa] p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <nav
            aria-label="Store collections"
            className="flex scrollbar-thin gap-2 overflow-x-auto pb-1"
          >
            {availableCollections.map((collection) => (
              <a
                key={collection.slug}
                href={`#${collection.slug}`}
                className={`font-sora inline-flex min-h-12 shrink-0 items-center gap-3 rounded-full border px-5 text-sm font-semibold transition-colors ${
                  collection.tone === "release"
                    ? "border-[#07111f] bg-[#07111f] text-white hover:bg-[#13253d]"
                    : "border-[#b9d9ff] bg-[#edf6ff] text-[#1458a9] hover:bg-[#dcecff]"
                }`}
              >
                {collection.name}
                <span className="font-mono text-xs opacity-60">
                  {String(collection.products.length).padStart(2, "0")}
                </span>
              </a>
            ))}
          </nav>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="border-border focus-within:border-primary/50 focus-within:ring-primary/20 flex min-h-12 items-center gap-2 rounded-full border bg-white px-4 shadow-2xs transition-all focus-within:ring-2">
              <Search className="text-secondary h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Search products</span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search products"
                className="placeholder:text-secondary text-foreground w-full bg-transparent text-sm outline-none sm:w-52"
              />
            </label>
            <button
              type="button"
              onClick={() => setCartOpen(true)}
              className="font-sora border-primary text-primary hover:bg-primary inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-full border bg-white px-5 text-sm font-semibold shadow-2xs transition-all hover:text-white"
            >
              <ShoppingBag className="h-4 w-4" aria-hidden="true" />
              Cart ({cartCount})
            </button>
          </div>
        </div>

        <div
          className="border-border mt-4 flex scrollbar-thin gap-2 overflow-x-auto border-t pt-4 pb-1"
          role="group"
          aria-label="Product category"
        >
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              aria-pressed={activeCategory === category}
              className={`font-sora min-h-11 shrink-0 cursor-pointer rounded-full border px-5 text-sm font-semibold transition-all ${
                activeCategory === category
                  ? "border-[#07111f] bg-[#07111f] text-white"
                  : "border-border hover:border-primary/40 text-foreground bg-transparent hover:bg-white"
              }`}
            >
              {category}
            </button>
          ))}
          <span className="sr-only" role="status" aria-live="polite">
            {visibleProductCount} products shown
          </span>
        </div>
      </div>

      {visibleProductCount > 0 ? (
        <div className="flex flex-col gap-20 md:gap-28">
          {visibleCollections.map((collection) => (
            <CollectionSection
              key={collection.slug}
              collection={collection}
              visibleCount={collection.products.length}
              totalCount={
                availableCollections.find(
                  (item) => item.slug === collection.slug,
                )?.products.length ?? collection.products.length
              }
            />
          ))}
        </div>
      ) : (
        <div className="border-border bg-primary/5 rounded-[1.75rem] border p-8 text-center sm:p-12">
          <p className="font-sora text-foreground text-lg font-semibold">
            Nothing matches that yet.
          </p>
          <p className="text-secondary mt-2 text-sm">
            Try a different category or clear your search.
          </p>
          <button
            type="button"
            onClick={() => {
              setActiveCategory("All");
              setQuery("");
            }}
            className="font-sora text-primary mt-4 cursor-pointer text-sm font-semibold hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

type CollectionSectionProps = Readonly<{
  collection: StoreCollection;
  visibleCount: number;
  totalCount: number;
}>;

const CollectionSection = ({
  collection,
  visibleCount,
  totalCount,
}: CollectionSectionProps): React.ReactElement => {
  const isRelease = collection.tone === "release";

  return (
    <section id={collection.slug} className="scroll-mt-8">
      <header
        className={`relative overflow-hidden px-6 py-8 sm:px-9 sm:py-10 lg:px-12 ${
          isRelease
            ? "rounded-2xl border border-[#142741] bg-[#07111f] text-white"
            : "border-t-2 border-[#217ff1] bg-white text-[#123f75]"
        }`}
      >
        <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p
              className={`font-mono text-xs font-semibold tracking-[0.14em] uppercase ${
                isRelease ? "text-[#72aefa]" : "opacity-60"
              }`}
            >
              {collection.eyebrow}
            </p>
            <h3
              className={`${isRelease ? "font-mono" : "font-sora"} mt-4 text-3xl leading-none font-semibold tracking-[-0.045em] sm:text-4xl md:text-5xl`}
            >
              {collection.name}
            </h3>
            <p
              className={`mt-5 max-w-2xl text-base leading-relaxed sm:text-lg ${
                isRelease ? "text-white/62" : "opacity-65"
              }`}
            >
              {collection.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 lg:justify-end">
            {isRelease && (
              <span className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 font-mono text-xs text-white/70">
                tag / v1.0.0
              </span>
            )}
            <span
              className={`rounded-full border px-4 py-2 font-mono text-xs ${
                isRelease
                  ? "border-white/15 bg-white/[0.06] text-white/70"
                  : "border-current/15 bg-white/40"
              }`}
            >
              {visibleCount === totalCount
                ? `${totalCount} products`
                : `${visibleCount} of ${totalCount} products`}
            </span>
          </div>
        </div>
      </header>

      <div className="mt-7 sm:mt-9">
        {collection.products.length > 0 ? (
          <ProductGrid products={collection.products} />
        ) : (
          <div className="border-border rounded-2xl border border-dashed bg-[#fafafa] p-8 text-center">
            <p className="font-sora text-secondary text-sm">
              No matching products in this collection.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
